<template>
  <div class="sign-er-wrapper">
    <div class="route-name">{{ $t(`message.sideMenu.${this.$route.name}`) }}</div>
    <div class="sign-er-container">
      <div class="pdf-loading" v-if="isPreviewLoading">
        <base-spinner :is-loading="true" :is-local="true" />
      </div>
      <iframe
        v-if="documentViewerUrl && !isPreviewLoading"
        :src="documentViewerUrl"
        class="er-document"
        title="early-repayment-notification"
      />
      <div class="preview-info" v-if="previewData && !isPreviewLoading">
        {{ $t('message.profile.borrower.earlyRepayment.notifyModal.availableFrom') }}
        {{ formatDate(previewData.earliestRepaymentDate) }}
      </div>
      <div class="sign-block" v-if="!isPreviewLoading">
        <div class="sign-title">{{ $t('message.profile.borrower.earlyRepayment.notifyModal.signTitle') }}</div>
        <div class="sign-offer-item" @click.stop="checkboxHandler()">
          <div
            class="check-box-btn"
            :class="{ 'check-box-checked': isNotificationAgreed }"
          >
            <img
              v-if="isNotificationAgreed"
              class="check-box-image"
              src="../../../public/custom/images/check.svg"
              alt="Checked image"
            />
          </div>
          <div class="check-box-label">
            {{ $t('message.profile.borrower.earlyRepayment.notifyModal.agreement') }}
          </div>
        </div>
        <div class="otp-container">
          <div class="otp-block">
            <base-text-input
              :mask-pattern="'######'"
              :name="'earlyRepaymentOtp'"
              :validate-rules="'otp'"
              :placeholder="$t('message.contract.otp.placeholder')"
              v-model="otp"
              :disabled="!isNotificationAgreed || isSubmitting"
              :is-has-error-block="false"
            />
          </div>
          <div class="send-otp-btn">
            <base-button
              :text="$t('message.profile.borrower.earlyRepayment.notifyModal.sendOtp')"
              :disabled="isSendOtpDisabled"
              :is-loading="isSendingOtp"
              @click="sendOtp()"
              min-height="40px"
            />
          </div>
        </div>
        <div class="borrower-block">
          <div class="borrower-title">{{ $t('message.profile.borrower.earlyRepayment.notifyModal.borrowerTitle') }}</div>
          <div class="borrower-full-name">{{ borrowerFullName }}</div>
        </div>
        <div class="sign-er-actions">
          <div class="sign-er-btn">
            <base-button
              :text="$t('message.profile.borrower.earlyRepayment.notifyModal.confirmBtn')"
              :disabled="isConfirmDisabled"
              :is-loading="isSubmitting"
              @click="confirmNotify()"
              min-height="40px"
            />
          </div>
          <div class="cancel-er-btn">
            <base-button
              :text="$t('message.profile.borrower.earlyRepayment.notifyModal.cancelBtn')"
              :disabled="isSubmitting"
              @click="goBack()"
              min-height="40px"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseTextInput from '../../components/base/BaseTextInput.vue';
import BaseButton from '../../components/base/BaseButton.vue';
import BaseSpinner from '../../components/base/BaseSpinner.vue';
import { mapGetters } from 'vuex';
import { formatEarlyRepaymentDate, resolveEarlyRepaymentErrorMessage } from '../../helpers/earlyRepayment';

export default {
  name: 'SignEarlyRepaymentNotify',
  inject: ['$httpService'],
  components: {
    BaseTextInput,
    BaseButton,
    BaseSpinner,
  },
  data() {
    return {
      blobUrl: '',
      previewData: null,
      otp: '',
      isPreviewLoading: true,
      isSendingOtp: false,
      isSubmitting: false,
      isNotificationAgreed: false,
      isOtpRequested: false,
    };
  },
  computed: {
    ...mapGetters(['profile']),
    loanPublicId() {
      return this.$route.params.id;
    },
    documentViewerUrl() {
      if (!this.blobUrl) {
        return '';
      }

      return `${this.blobUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&zoom=100`;
    },
    borrowerFullName() {
      const lastName = this.profile?.name_ru?.family_name_ru || '';
      const firstName = this.profile?.name_ru?.given_name_ru || '';
      const middleName = this.profile?.name_ru?.middle_name_ru || '';

      return [lastName, firstName, middleName].filter(Boolean).join(' ');
    },
    isSendOtpDisabled() {
      return !this.isNotificationAgreed || this.isSendingOtp || this.isSubmitting;
    },
    isConfirmDisabled() {
      return !this.isNotificationAgreed || !this.otp || this.errors.has('earlyRepaymentOtp') || this.isSubmitting;
    },
  },
  async created() {
    this.$store.dispatch('loading', true);

    try {
      await this.loadPreview();
    } finally {
      this.$store.dispatch('loading', false);
    }
  },
  beforeDestroy() {
    this.revokeDocumentUrl();
  },
  methods: {
    formatDate(dateStr) {
      return formatEarlyRepaymentDate(dateStr);
    },
    goBack() {
      if (!this.isSubmitting) {
        this.$router.push({ name: 'profile' });
      }
    },
    checkboxHandler() {
      if (this.isSubmitting) {
        return;
      }

      this.isNotificationAgreed = !this.isNotificationAgreed;

      if (!this.isNotificationAgreed) {
        this.otp = '';
        this.isOtpRequested = false;
      }
    },
    async loadPreview() {
      this.isPreviewLoading = true;

      try {
        const response = await this.$httpService.loan.getEarlyRepaymentPreview(this.loanPublicId);
        this.previewData = this.parsePreviewMeta(response);
        this.showDocument(response);
      } catch (error) {
        this.$toast.error(resolveEarlyRepaymentErrorMessage(error, (key, params) => this.$t(key, params)));
        this.$router.push({ name: 'profile' });
      } finally {
        this.isPreviewLoading = false;
      }
    },
    parsePreviewMeta(response) {
      const headers = response.headers || {};
      const earliestRepaymentDate = headers['earliest-repayment-date']
        || headers['x-earliest-repayment-date']
        || headers.earliestrepaymentdate
        || null;

      return earliestRepaymentDate ? { earliestRepaymentDate } : null;
    },
    showDocument(response) {
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });

      this.revokeDocumentUrl();
      this.blobUrl = window.URL.createObjectURL(blob);
    },
    revokeDocumentUrl() {
      if (this.blobUrl) {
        window.URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = '';
      }
    },
    async sendOtp() {
      if (this.isSendOtpDisabled) {
        return;
      }

      this.isSendingOtp = true;

      try {
        await this.$httpService.loan.sendEarlyRepaymentOtp(this.loanPublicId);
        this.isOtpRequested = true;
        this.$toast.success(this.$t('message.signIn.otpToastMsg'));
      } catch (error) {
        this.$toast.error(resolveEarlyRepaymentErrorMessage(error, (key, params) => this.$t(key, params)));
      } finally {
        this.isSendingOtp = false;
      }
    },
    async confirmNotify() {
      if (this.isConfirmDisabled) {
        return;
      }

      this.isSubmitting = true;

      try {
        await this.$httpService.loan.notifyEarlyRepayment({
          loan_public_id: this.loanPublicId,
          otp: this.otp,
        });
        this.$toast.success(this.$t('message.profile.borrower.earlyRepayment.notifyModal.success'));
        this.goToProfile();
      } catch (error) {
        const data = error?.response?.data || {};

        if (data.message === 'loan.early-repayment.already-notified') {
          this.goToProfile();
          return;
        }

        this.$toast.error(resolveEarlyRepaymentErrorMessage(error, (key, params) => this.$t(key, params)));
      } finally {
        this.isSubmitting = false;
      }
    },
    goToProfile() {
      this.$router.push({
        name: 'profile',
        params: this.$route.params.backRouteParams || {}
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sign-er-wrapper {
  @include page-wrapper-with-header-and-footer;

  padding-top: 40px;
  box-sizing: border-box;
}

.sign-er-container {
  @include base-page-container;

  flex-direction: column;
  min-height: calc(100vh - 434px);
}

.route-name {
  @include route-name;
  width: 95%;
  text-align: start;
  margin-bottom: 20px;
  margin-top: -20px;
}

.pdf-loading {
  width: 100%;
  min-height: 700px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.er-document {
  width: 100%;
  flex: 1;
  min-height: 700px;
  border: none;
  border-radius: 10px;
  background-color: #ffffff;
}

.preview-info {
  margin-top: 16px;
  font-size: 14px;
  line-height: 19px;
  color: #333333;
}

.sign-block {
  width: 100%;
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  margin-bottom: 60px;
}

.sign-title {
  margin-bottom: 20px;
  font-weight: 700;
  font-size: 16px;
  line-height: 135%;
  color: #121212;
}

.sign-offer-item {
  margin-bottom: 30px;
  display: flex;
  align-items: flex-start;
  cursor: pointer;
}

.check-box-btn {
  @include base-check-box;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.check-box-image {
  width: 12px;
}

.check-box-label {
  @include base-check-box-label;

  font-size: 16px;
  line-height: 135%;
  font-weight: 500;
}

.otp-container {
  display: flex;
  user-select: none;
}

.otp-block {
  width: 280px;
  margin-right: 20px;
}

.send-otp-btn {
  width: 200px;
  height: 40px;
}

.borrower-block {
  width: 100%;
  margin-top: 30px;
  display: flex;
  flex-direction: column;
}

.borrower-title {
  margin-bottom: 10px;
  font-weight: 600;
  font-size: 16px;
  line-height: 135%;
  color: #121212;
}

.borrower-full-name {
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #121212;
  text-transform: uppercase;
}

.sign-er-actions {
  margin-top: 30px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.sign-er-btn {
  width: 260px;
  height: 40px;
}

.cancel-er-btn {
  width: 160px;
  height: 40px;
}

.cancel-er-btn ::v-deep .button {
  background-color: #ffffff;
  border: 2px solid #cdcdcd;
  color: #333333;
}
</style>
